<template>
  <main>
    <div class="container py-5">
      <!-- Header -->
      <div class="ticket-header mb-4">
        <div class="d-flex align-items-center gap-3 mb-2">
          <div class="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                 stroke-linejoin="round">
              <path d="M15 5v2"/>
              <path d="M15 11v2"/>
              <path d="M15 17v2"/>
              <path
                  d="M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z"/>
            </svg>
          </div>
          <div>
            <h2 class="mb-0 fw-bold">Create New Ticket</h2>
            <p class="text-muted mb-0 mt-1">Submit a support request and we'll get back to you shortly.</p>
          </div>
        </div>
      </div>

      <!-- Logged-in User Info Banner -->
      <transition name="fade">
        <div v-if="isUserLoggedIn && studentVerified" class="logged-in-banner mb-4">
          <div class="d-flex align-items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
              <path d="m9 12 2 2 4-4"/>
            </svg>
            <span>You are logged in as <strong>{{ form.custom_student_name }}</strong>. Your contact information has been auto-filled.</span>
          </div>
        </div>
      </transition>

      <!-- Form Card -->
      <div class="ticket-form-card">
        <form @submit.prevent="submitTicket">
          <!-- Progress Steps -->
          <div class="form-progress mb-4">
            <div class="progress-step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
              <div class="step-circle">
                <svg v-if="currentStep > 1" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <path d="M20 6 9 17l-5-5"/>
                </svg>
                <span v-else>1</span>
              </div>
              <span class="step-label">Contact Info</span>
            </div>
            <div class="progress-line" :class="{ active: currentStep > 1 }"></div>
            <div class="progress-step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
              <div class="step-circle">
                <svg v-if="currentStep > 2" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <path d="M20 6 9 17l-5-5"/>
                </svg>
                <span v-else>2</span>
              </div>
              <span class="step-label">Classification</span>
            </div>
            <div class="progress-line" :class="{ active: currentStep > 2 }"></div>
            <div class="progress-step" :class="{ active: currentStep >= 3 }">
              <div class="step-circle">3</div>
              <span class="step-label">Details</span>
            </div>
          </div>


          <transition name="slide-fade" mode="out-in">
            <!-- Step 1: Contact Info -->
            <div v-if="currentStep === 1" key="step1">
              <div class="section-title">
                <h5 class="fw-semibold mb-1">Contact Information</h5>
                <p class="text-muted small">
                  {{ isUserLoggedIn && studentVerified 
                    ? 'Your contact details have been automatically filled from your account.' 
                    : 'Provide your contact details. We\'ll verify your student record.' }}
                </p>
              </div>
              <div class="row g-3">
                <!-- Mobile Phone (FIRST - so user enters it first) -->
                <div class="col-md-6">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Mobile Phone (رقم الهاتف) <span class="text-danger">*</span>
                      <span v-if="isUserLoggedIn && studentVerified" class="auto-filled-badge">
                        From Account
                      </span>
                    </label>
                    <div class="input-with-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                           fill="none" stroke="currentColor" stroke-width="2" class="input-icon">
                        <rect width="14" height="20" x="5" y="2" rx="2" ry="2"/>
                        <path d="M12 18h.01"/>
                      </svg>
                     <input
                        type="tel"
                        v-model="form.custom_mobile_phone"
                        class="form-input-custom"
                        :class="{
                          'is-invalid': errors.custom_mobile_phone,
                          'is-verified': studentVerified,
                          'is-readonly': isUserLoggedIn && studentVerified
                        }"
                        :readonly="isUserLoggedIn && studentVerified"
                        placeholder="Enter mobile number"
                        @input="onMobileInput"
                        @blur="onMobileBlur"
                        @keypress="preventLettersInTel"
                        required
                      />
                      <!-- Verification status icon -->
                      <div class="input-status-icon" v-if="studentVerified">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                             viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2.5">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <path d="m9 11 3 3L22 4"/>
                        </svg>
                      </div>
                    </div>
                    <span v-if="errors.custom_mobile_phone" class="error-text">
                      {{ errors.custom_mobile_phone }}
                    </span>
                    <span v-if="studentVerified && !isUserLoggedIn" class="verified-text">
                      ✓ Student verified successfully
                    </span>
                    <span v-if="isUserLoggedIn && studentVerified" class="readonly-hint">
                      Fetched from your account
                    </span>
                  </div>
                </div>

                <!-- Student Name -->
                <div class="col-md-6">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Student Name (اسم الطالب)
                      <span class="text-danger">*</span>
                      <span v-if="studentVerified" class="auto-filled-badge">
                        {{ isUserLoggedIn ? 'From Account' : 'Auto-filled' }}
                      </span>
                    </label>
                    <div class="input-with-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                           fill="none" stroke="currentColor" stroke-width="2" class="input-icon">
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      <input type="text" v-model="form.custom_student_name"
                             class="form-input-custom"
                             :class="{
                               'is-invalid': errors.custom_student_name,
                               'is-readonly': studentVerified
                             }"
                             :readonly="studentVerified"
                             placeholder="Will be auto-filled after verification"
                             required />
                    </div>
                    <span v-if="errors.custom_student_name" class="error-text">
                      {{ errors.custom_student_name }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Student Info Card (shown after verification) -->
              <transition name="fade">
                <div v-if="studentVerified" class="student-info-card mt-3">
                  <div class="d-flex align-items-center gap-2 mb-2">
                    <div class="student-avatar">
                      {{ getInitials(form.custom_student_name) }}
                    </div>
                    <div>
                      <div class="fw-semibold">{{ form.custom_student_name }}</div>
                      <div class="text-muted small">{{ studentId }}</div>
                    </div>
                    <div v-if="isUserLoggedIn" class="ms-auto">
                      <span class="logged-in-badge">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                        </svg>
                        Logged In
                      </span>
                    </div>
                  </div>
                  <div class="student-meta">
                    <div class="meta-item" v-if="form.custom_branch">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span>{{ form.custom_branch }}</span>
                    </div>
                    <div class="meta-item" v-if="form.custom_batch">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      <span>{{ form.custom_batch }}</span>
                    </div>
                    <div class="meta-item" v-if="form.custom_program">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                        <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                      </svg>
                      <span>{{ form.custom_program }}</span>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Step 2: Classification -->
            <div v-else-if="currentStep === 2" key="step2">
              <div class="section-title">
                <h5 class="fw-semibold mb-1">Classification</h5>
                <p class="text-muted small">Help us categorize your request.</p>
              </div>
              <div class="row g-3">
                <!-- Ticket Type -->
                <div class="col-md-6">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Ticket Type (نوع التذكرة) <span class="text-danger">*</span>
                    </label>
                    <div class="custom-select-wrapper">
                      <select v-model="form.ticket_type" class="form-select-custom"
                              :class="{ 'is-invalid': errors.ticket_type }" 
                              @change="onTicketTypeChange"
                              required>
                        <option value="" disabled>Select ticket type...</option>
                        <option v-for="opt in options.ticket_type" :key="opt.name" :value="opt.name">
                          {{ opt.name }}
                        </option>
                      </select>
                      <div class="select-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" stroke-width="2">
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                      </div>
                    </div>
                    <span v-if="errors.ticket_type" class="error-text">{{ errors.ticket_type }}</span>
                  </div>
                </div>

                <!-- Request Type (NEW - Conditional based on Ticket Type) -->
                <div class="col-md-6" v-if="requestTypeOptions.length > 0">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Request Type (نوع الطلب) <span class="text-danger">*</span>
                    </label>
                    <div class="custom-select-wrapper">
                      <select v-model="form.custom_request_type" class="form-select-custom"
                              :class="{ 'is-invalid': errors.custom_request_type }" required>
                        <option value="" disabled>Select request type...</option>
                        <option v-for="opt in requestTypeOptions" :key="opt" :value="opt">
                          {{ opt }}
                        </option>
                      </select>
                      <div class="select-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" stroke-width="2">
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                      </div>
                    </div>
                    <span v-if="errors.custom_request_type" class="error-text">{{ errors.custom_request_type }}</span>
                  </div>
                </div>

                <!-- Priority -->
                <div class="col-md-6">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Priority (الأولوية) <span class="text-danger">*</span>
                    </label>
                    <div class="custom-select-wrapper">
                      <select v-model="form.priority" class="form-select-custom"
                              :class="{ 'is-invalid': errors.priority }" required>
                        <option value="" disabled>Select priority...</option>
                        <option v-for="opt in options.priority" :key="opt.name" :value="opt.name">
                          {{ opt.name }}
                        </option>
                      </select>
                      <div class="select-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor" stroke-width="2">
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                      </div>
                    </div>
                    <span v-if="errors.priority" class="error-text">{{ errors.priority }}</span>
                  </div>
                </div>

                <!-- Branch (readonly if auto-filled) -->
                <div class="col-md-6">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Branch (الفرع) <span class="text-danger">*</span>
                      <span v-if="studentVerified && form.custom_branch" class="auto-filled-badge">
                        Auto-filled
                      </span>
                    </label>
                    <div class="custom-select-wrapper">
                      <select v-model="form.custom_branch" class="form-select-custom"
                              :class="{
                                'is-invalid': errors.custom_branch,
                                'is-readonly': branchReadonly
                              }"
                              :disabled="branchReadonly"
                              required>
                        <option value="" disabled>Select branch...</option>
                        <option v-for="opt in options.custom_branch" :key="opt.name" :value="opt.name">
                          {{ opt.name }}
                        </option>
                      </select>
                      <div class="select-icon">
                        <svg v-if="!branchReadonly" xmlns="http://www.w3.org/2000/svg" width="18"
                             height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2">
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                             viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2">
                          <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </div>
                    </div>
                    <span v-if="errors.custom_branch" class="error-text">{{ errors.custom_branch }}</span>
                    <span v-if="branchReadonly" class="readonly-hint">
                      Fetched from student record
                    </span>
                  </div>
                </div>

                <!-- Batch (readonly if auto-filled) -->
                <div class="col-md-6">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Batch (الدفعة)
                      <span v-if="studentVerified && form.custom_batch" class="auto-filled-badge">
                        Auto-filled
                      </span>
                    </label>
                    <div class="custom-select-wrapper">
                      <select v-model="form.custom_batch" class="form-select-custom"
                              :class="{
                                'is-invalid': errors.custom_batch,
                                'is-readonly': batchReadonly
                              }"
                              :disabled="batchReadonly"
                              >
                        <option value="" disabled>Select batch...</option>
                        <option v-for="opt in options.custom_batch" :key="opt.name" :value="opt.name">
                          {{ opt.name }}
                        </option>
                      </select>
                      <div class="select-icon">
                        <svg v-if="!batchReadonly" xmlns="http://www.w3.org/2000/svg" width="18"
                             height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="2">
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                             viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2">
                          <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </div>
                    </div>
                    <span v-if="errors.custom_batch" class="error-text">{{ errors.custom_batch }}</span>
                    <span v-if="batchReadonly" class="readonly-hint">
                      Fetched from student record
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Ticket Details -->
            <div v-else-if="currentStep === 3" key="step3">
              <div class="section-title">
                <h5 class="fw-semibold mb-1">Ticket Details</h5>
                <p class="text-muted small">Describe your issue in detail.</p>
              </div>
              <div class="row g-3">
                <!-- Subject -->
                <div class="col-12">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Subject (الموضوع) <span class="text-danger">*</span>
                    </label>
                    <div class="input-with-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                           fill="none" stroke="currentColor" stroke-width="2" class="input-icon">
                        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0
                        0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
                        <path d="M18 14h-8"/>
                        <path d="M15 18h-5"/>
                        <path d="M10 6h8v4h-8V6Z"/>
                      </svg>
                      <input type="text" v-model="form.subject"
                             class="form-input-custom"
                             :class="{ 'is-invalid': errors.subject }"
                             placeholder="Brief summary of your issue" required />
                    </div>
                    <span v-if="errors.subject" class="error-text">{{ errors.subject }}</span>
                  </div>
                </div>

                <!-- Description -->
                <div class="col-12">
                  <div class="form-floating-custom">
                    <label class="form-label-custom">
                      Description (الوصف) <span class="text-danger">*</span>
                    </label>
                    <textarea v-model="form.description" class="form-textarea-custom"
                              :class="{ 'is-invalid': errors.description }"
                              placeholder="Please provide as much detail as possible..."
                              rows="6" required></textarea>
                    <div class="char-counter text-muted small mt-1">
                      {{ form.description.length }} characters
                    </div>
                    <span v-if="errors.description" class="error-text">{{ errors.description }}</span>
                  </div>
                </div>
              </div>

              <!-- Review Summary -->
              <div class="review-summary mt-4" v-if="hasAnyData">
                <h6 class="fw-semibold mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                       fill="none" stroke="currentColor" stroke-width="2" class="me-2">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Review Summary (ملخص المراجعة)
                </h6>
                <div class="summary-grid">
                  <div class="summary-item" v-if="form.custom_student_name">
                    <span class="summary-label">Student (الطالب)</span>
                    <span class="summary-value">{{ form.custom_student_name }}</span>
                  </div>
                  <div class="summary-item" v-if="form.custom_program">
                    <span class="summary-label">Program (البرنامج)</span>
                    <span class="summary-value">{{ form.custom_program }}</span>
                  </div>
                  <div class="summary-item" v-if="form.custom_mobile_phone">
                    <span class="summary-label">Mobile (رقم الهاتف)</span>
                    <span class="summary-value">{{ form.custom_mobile_phone }}</span>
                  </div>
                  <div class="summary-item" v-if="form.ticket_type">
                    <span class="summary-label">Type (النوع)</span>
                    <span class="summary-value">{{ form.ticket_type }}</span>
                  </div>
                  <!-- NEW: Request Type in summary -->
                  <div class="summary-item" v-if="form.custom_request_type">
                    <span class="summary-label">Request Type (نوع الطلب)</span>
                    <span class="summary-value">{{ form.custom_request_type }}</span>
                  </div>
                  <div class="summary-item" v-if="form.priority">
                    <span class="summary-label">Priority (الأولوية)</span>
                    <span class="summary-value">
                      <span class="priority-badge" :class="getPriorityClass(form.priority)">
                        {{ form.priority }}
                      </span>
                    </span>
                  </div>
                  <div class="summary-item" v-if="form.custom_branch">
                    <span class="summary-label">Branch (الفرع)</span>
                    <span class="summary-value">{{ form.custom_branch }}</span>
                  </div>
                  <div class="summary-item" v-if="form.custom_batch">
                    <span class="summary-label">Batch (الدفعة)</span>
                    <span class="summary-value">{{ form.custom_batch }}</span>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <!-- Action Buttons -->
          <div class="form-actions mt-4">
            <button v-if="currentStep > 1" type="button" class="btn-secondary-custom"
                    @click="prevStep" :disabled="submitting">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" stroke-width="2">
                <path d="m15 18-6-6 6-6"/>
              </svg>
              Back
            </button>
            <div class="ms-auto d-flex gap-2">
              <button v-if="currentStep < 3" type="button" class="btn-primary-custom"
                      @click="nextStep" :disabled="validatingMobile || fetchingUserData">
                <span v-if="validatingMobile || fetchingUserData" class="spinner-border spinner-border-sm me-2"></span>
                {{ validatingMobile ? 'Verifying...' : (fetchingUserData ? 'Loading...' : 'Next') }}
                <svg v-if="!validatingMobile && !fetchingUserData" xmlns="http://www.w3.org/2000/svg" width="18"
                     height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2">
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </button>
              <button v-else type="submit" class="btn-submit-custom" :disabled="submitting">
                <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                     class="me-1">
                  <path d="m22 2-7 20-4-9-9-4Z"/>
                  <path d="M22 2 11 13"/>
                </svg>
                {{ submitting ? 'Submitting...' : 'Submit Ticket' }}
              </button>
            </div>
          </div>
        </form>
      </div>
      <div class="diffcanlt_use">
        <p class="text-muted small mb-0 text-right">
         لضمان سرعة ودقة متابعة طلبكم، يُرجى استخدام نظام التذاكر، حيث يتم التعامل مع الطلبات من خلاله بشكل أساسي
        </p>
        <p class="text-muted small mb-0 text-right">
          في حال وجود مشكلة تقنية فقط، يمكن التواصل عبر واتساب: 
          <strong>01070726428</strong>
        </p>
      </div>
      <!-- Success Modal -->
      <transition name="fade">
        <div v-if="showSuccess" class="success-overlay" @click.self="closeSuccess">
          <div class="success-modal">
            <div class="success-icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <path d="m9 11 3 3L22 4"/>
              </svg>
            </div>
            <h4 class="fw-bold mt-3">Ticket Created!</h4>
            <p class="text-muted" v-if="createdTicketId">
              Your ticket <strong>#{{ createdTicketId }}</strong> has been submitted successfully.
            </p>
            <p class="text-muted small">Our team will review your request and respond shortly.</p>
            <button class="btn-primary-custom mt-3" @click="closeSuccess">
              Create Another Ticket
            </button>
          </div>
        </div>
      </transition>
    </div>
  </main>
</template>

<script>
import { authData, isLoggedIn } from "../../utils/auth";

export default {
  name: "Helpdesk",
  setup() {
    return { isLoggedIn };
  },
  data() {
    return {
      custom_program: "", 
      lms_website: lms_website,
      authData: authData(),
      moment: moment,
      currentStep: 1,
      submitting: false,
      showSuccess: false,
      createdTicketId: null,

      // Student validation state
      validatingMobile: false,
      studentVerified: false,
      studentId: null,
      branchReadonly: false,
      batchReadonly: false,
      lastValidatedMobile: null,

      // NEW: User session state
      isUserLoggedIn: false,
      fetchingUserData: false,

      mobileDebounceTimer: null,

      // NEW: Request Type options mapping
      requestTypeMapping: {
        "طلب / استفسار عميل": [
          "مشكلة حضور",
          "مشكلة أقساط / سداد",
          "تغيير باتش أو مواعيد",
          "دخول المنصة",
          "طلب شهادة",
          "شكوى أو ملاحظة",
          "اخري"
        ],
        "طلب / استفسار محاضر": [
          "تعارض مواعيد",
          "مشكلة حساب ساعات / أجر",
          "طلب مواد تعليمية",
          "تصعيد مشكلة طالب",
          "اخري"
        ],
        "التشغيل": [
          "مشكلة فرع",
          "قاعة / أجهزة",
          "إنترنت / منصة",
          "مشكلة System",
          "الشهادات",
          "الحسابات",
          "اخري"
        ]
      },

      form: {
        ticket_type: "",
        custom_request_type: "", // NEW field
        priority: "Medium",
        custom_branch: "",
        custom_batch: "",
        custom_student_name: "",
        custom_mobile_phone: "",
        subject: "",
        description: "",
      },
      options: {
        ticket_type: [],
        priority: [],
        custom_branch: [],
        custom_batch: [],
      },
      errors: {},
    };
  },
  computed: {
    auth() {
      if (this.authData && this.authData.is_logged_in && this.authData.user) {
        return this.authData;
      }
    },
    hasAnyData() {
      return (
        this.form.ticket_type ||
        this.form.priority ||
        this.form.custom_branch ||
        this.form.custom_batch ||
        this.form.custom_program || 
        this.form.custom_student_name ||
        this.form.custom_mobile_phone ||
        this.form.custom_request_type // NEW
      );
    },
    // NEW: Computed property for Request Type options based on Ticket Type
    requestTypeOptions() {
      if (this.form.ticket_type && this.requestTypeMapping[this.form.ticket_type]) {
        return this.requestTypeMapping[this.form.ticket_type];
      }
      return [];
    },
  },
  methods: {
    async fetchOptions(doctype, key) {
      try {
        const res = await frappe.call({
          method: "lms_website.api.helpdesk.get_options",
          args: { 
            doctype: doctype,
            filters: {
              ...(doctype === "HD Ticket Type" ? { name: ["!=","طلب / استفسار محاضر"] } : {})
            }
         },
        });
        if (res && res.message) {
          this.options[key] = res.message;
        }
      } catch (err) {
        console.error(`Error fetching ${doctype}:`, err);
        this.options[key] = [];
      }
    },

    // NEW: Reset request type when ticket type changes
    onTicketTypeChange() {
      this.form.custom_request_type = "";
      // Clear any existing error for request type
      if (this.errors.custom_request_type) {
        delete this.errors.custom_request_type;
      }
    },

    // Reset student verification when mobile number changes
    // async onMobileChange() {
    //   const cleaned = event.target.value.replace(/\D/g, '');

    //   this.form.custom_mobile_phone = cleaned;
    //   // Don't allow changes if user is logged in
    //   if (this.isUserLoggedIn && this.studentVerified) {
    //     return;
    //   }
      
    //   if (this.form.custom_mobile_phone !== this.lastValidatedMobile) {
    //     this.studentVerified = false;
    //     this.studentId = null;
    //     this.branchReadonly = false;
    //     this.batchReadonly = false;
    //     this.form.custom_student_name = "";
    //     this.form.custom_branch = "";
    //     this.form.custom_batch = "";
    //     await this.validateStudentMobile()
    //   }
    // },

    preventLettersInTel(event) {
      const char = String.fromCharCode(event.which || event.keyCode);

      // Allow: digits  +  -  (  )  space
      if (!/[\d\+\-\(\)\s]/.test(char)) {
        event.preventDefault();
        this.errors.custom_mobile_phone =
          'Letters are not allowed – numbers only (الأرقام فقط مسموح بها في هذا الحقل)';
      }
    },

    onMobileInput(event) {
      const rawValue = event.target.value;

      /* ── Requirement 2: detect pasted / auto-filled letters ── */
      if (/[a-zA-Z\u0600-\u06FF]/.test(rawValue)) {
        this.errors.custom_mobile_phone =
          'Letters are not allowed – numbers only (الأرقام فقط مسموح بها في هذا الحقل)';
      } else if (
        this.errors.custom_mobile_phone ===
        'Letters are not allowed – numbers only (الأرقام فقط مسموح بها في هذا الحقل)'
      ) {
        // Clear only the "letters" error; keep other errors untouched
        this.errors.custom_mobile_phone = '';
      }

      /* ── Strip everything except digits and leading + ── */
      const cleaned = rawValue.replace(/[^\d+]/g, '');
      this.form.custom_mobile_phone = cleaned;

      /* ── Skip if logged-in user is already verified ── */
      if (this.isUserLoggedIn && this.studentVerified) return;

      /* ── Reset previous verification when the number changes ── */
      if (cleaned !== this.lastValidatedMobile) {
        this.studentVerified   = false;
        this.studentId         = null;
        this.branchReadonly     = false;
        this.batchReadonly      = false;
        this.form.custom_student_name = '';
        this.form.custom_branch       = '';
        this.form.custom_batch        = '';
      }

      /* ── Requirement 1: debounce – validate only after the user stops typing ── */
      clearTimeout(this.mobileDebounceTimer);

      // Fire the API call 1 s after the last keystroke AND only when
      // the number looks "complete" (≥ 10 digits — adjust to your format)
      if (cleaned.replace(/\D/g, '').length >= 10) {
        this.mobileDebounceTimer = setTimeout(() => {
          this.validateStudentMobile();
        }, 1000);                         // 1 second debounce
      }
    },

    onMobileBlur() {
      if (this.isUserLoggedIn && this.studentVerified) return;

      clearTimeout(this.mobileDebounceTimer);   // cancel any pending debounce

      const phone = (this.form.custom_mobile_phone || '').trim();
      if (!phone || phone.length < 7) return;   // too short to bother

      // Only call the API if this number hasn't been validated yet
      if (phone !== this.lastValidatedMobile) {
        this.validateStudentMobile();
      }
    },

    async fetchLoggedInUserData() {
      this.fetchingUserData = true;
      try {
        const res = await frappe.call({
          method: "lms_website.api.helpdesk.get_current_user_student_data",
        });

        if (res && res.message && res.message.valid) {
          const data = res.message;
          console.log("Fetched logged-in user data:", data);
          this.isUserLoggedIn = true;
          this.studentVerified = true;
          this.studentId = data.student_id;
          
          this.form.custom_mobile_phone = data.mobile_phone || "";
          this.form.custom_student_name = data.student_name || "";
          this.lastValidatedMobile = data.mobile_phone || "";

          if (data.branch) {
            this.form.custom_branch = data.branch;
            this.branchReadonly = true;
          }
          if (data.batch) {
            this.form.custom_batch = data.batch;
            this.batchReadonly = true;
          }
          
          // ── NEW ──────────────────────────────
          this.form.custom_program = data.program || "";
          // ─────────────────────────────────────

          return true;
        }
        return false;
      } catch (err) {
        console.error("Error fetching user data:", err);
        return false;
      } finally {
        this.fetchingUserData = false;
      }
    },

    async validateStudentMobile() {
      this.validatingMobile = true;
      this.errors = {};

      try {
        const res = await frappe.call({
          method: "lms_website.api.helpdesk.validate_student_mobile",
          args: { mobile_phone: this.form.custom_mobile_phone.trim() },
        });

        if (res && res.message) {
          const data = res.message;
          console.log(data)
          if (data.valid) {
            this.studentVerified = true;
            this.studentId = data.student_id;
            this.lastValidatedMobile = this.form.custom_mobile_phone.trim();
            this.form.custom_student_name = data.student_name || "";

            if (data.branch) {
              this.form.custom_branch = data.branch;
              this.branchReadonly = true;
            } else {
              this.form.custom_branch = "";
              this.branchReadonly = false;
            }
            if (data.batch) {
              this.form.custom_batch = data.batch;
              this.batchReadonly = true;
            } else {
              this.form.custom_batch = "";
              this.batchReadonly = false;
            }
            this.form.custom_program = data.program || "";
            return true;
          } else {
            // Student not found
            this.studentVerified = false;
            this.errors.custom_mobile_phone = data.message || "No student found with this mobile number.";
            return false;
          }
        }

        return false;
      } catch (err) {
        console.error("Error validating mobile:", err);
        this.errors.custom_mobile_phone = "Verification failed. Please try again.";
        return false;
      } finally {
        this.validatingMobile = false;
      }
    },

    validateStep(step) {
      this.errors = {};
      let valid = true;

      if (step === 1) {
        if (!this.form.custom_mobile_phone.trim()) {
          this.errors.custom_mobile_phone = "Mobile phone is required";
          valid = false;
        } else if (!/^[\d\s\-\+\(\)]{7,15}$/.test(this.form.custom_mobile_phone.trim())) {
          this.errors.custom_mobile_phone = "Enter a valid phone number";
          valid = false;
        }

        if (!this.form.custom_student_name.trim()) {
          this.errors.custom_student_name = "Student name is required";
          valid = false;
        }
      } else if (step === 2) {
        if (!this.form.ticket_type) {
          this.errors.ticket_type = "Ticket type is required";
          valid = false;
        }
        // NEW: Validate request type if options are available
        if (this.requestTypeOptions.length > 0 && !this.form.custom_request_type) {
          this.errors.custom_request_type = "Request type is required";
          valid = false;
        }
        if (!this.form.priority) {
          this.errors.priority = "Priority is required";
          valid = false;
        }
        if (!this.form.custom_branch) {
          this.errors.custom_branch = "Branch is required";
          valid = false;
        }
      } else if (step === 3) {
        if (!this.form.subject.trim()) {
          this.errors.subject = "Subject is required";
          valid = false;
        }
        if (!this.form.description.trim()) {
          this.errors.description = "Description is required";
          valid = false;
        } else if (this.form.description.trim().length < 10) {
          this.errors.description = "Description must be at least 10 characters";
          valid = false;
        }
      }

      return valid;
    },

    async nextStep() {
      if (!this.validateStep(this.currentStep)) return;

      // Step 1: Validate mobile against Student doctype before proceeding
      if (this.currentStep === 1) {
        // Skip validation if user is logged in and already verified
        if (this.isUserLoggedIn && this.studentVerified) {
          this.currentStep++;
          return;
        }
        
        // Skip re-validation if already verified with the same number
        if (
          this.studentVerified &&
          this.form.custom_mobile_phone.trim() === this.lastValidatedMobile
        ) {
          this.currentStep++;
          return;
        }

        const isValid = await this.validateStudentMobile();
        if (!isValid) return; // Don't proceed if validation fails
      }

      this.currentStep++;
    },

    prevStep() {
      this.errors = {};
      this.currentStep--;
    },

    async submitTicket() {
      if (!this.validateStep(3)) return;

      this.submitting = true;
      try {
        const res = await frappe.call({
          method: "lms_website.api.helpdesk.create_ticket",
          args: {
            data: {
              ticket_type: this.form.ticket_type,
              custom_request_type: this.form.custom_request_type, // NEW field
              priority: this.form.priority,
              custom_branch: this.form.custom_branch,
              custom_batch: this.form.custom_batch,
              custom_student_name: this.form.custom_student_name,
              custom_mobile_number: this.form.custom_mobile_phone,
              subject: this.form.subject,
              description: this.form.description,
            },
          },
        });

        if (res && res.message) {
          this.createdTicketId = res.message;
          this.showSuccess = true;
        }
      } catch (err) {
        console.error("Error creating ticket:", err);
        frappe.msgprint({
          title: "Error",
          indicator: "red",
          message: err.message || "Failed to create ticket. Please try again.",
        });
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      // Store logged-in state before reset
      const wasLoggedIn = this.isUserLoggedIn;
      
      this.form = {
        ticket_type: "",
        custom_request_type: "", // NEW field
        priority: "",
        custom_branch: "",
        custom_batch: "",
        custom_student_name: "",
        custom_mobile_phone: "",
        custom_program: "",
        subject: "",
        description: "",
      };
      this.errors = {};
      this.currentStep = 1;
      this.createdTicketId = null;
      
      // If user was logged in, re-fetch their data
      if (wasLoggedIn) {
        this.fetchLoggedInUserData();
      } else {
        this.studentVerified = false;
        this.studentId = null;
        this.branchReadonly = false;
        this.batchReadonly = false;
        this.lastValidatedMobile = null;
      }
    },

    closeSuccess() {
      this.showSuccess = false;
      this.resetForm();
    },

    getPriorityClass(priority) {
      const p = (priority || "").toLowerCase();
      if (p === "urgent" || p === "high") return "priority-high";
      if (p === "medium") return "priority-medium";
      return "priority-low";
    },

    getInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    },
  },

  async mounted() {
    this.fetchOptions("HD Ticket Type", "ticket_type");
    this.fetchOptions("HD Ticket Priority", "priority");
    this.fetchOptions("Cost Center", "custom_branch");
    this.fetchOptions("Student Batch Name", "custom_batch");

    // NEW: Check if user is logged in and fetch their data
    if (this.isLoggedIn) {
      await this.fetchLoggedInUserData();
    }

    setTimeout(() => {
      NProgress.done();
      $("[data-background]").each(function () {
        $(this).css(
          "background-image",
          "url( " + $(this).attr("data-background") + "  )"
        );
      });
    }, 500);
  },
  beforeUnmount() {
    clearTimeout(this.mobileDebounceTimer);
  },
};
</script>

<style scoped>
/* ============================================
   GENERAL / CARD
   ============================================ */
.ticket-header .header-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.ticket-form-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 6px 24px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
}

/* NEW: Logged-in banner styles */
.logged-in-banner {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  border-radius: 12px;
  padding: 14px 20px;
  color: #1e40af;
  font-size: 14px;
}

.logged-in-banner svg {
  color: #2563eb;
  flex-shrink: 0;
}

.logged-in-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* ============================================
   PROGRESS STEPS
   ============================================ */
.form-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px 24px;
  border-bottom: 1px solid #f3f4f6;
}

.progress-step {
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0.4;
  transition: all 0.3s ease;
}

.progress-step.active {
  opacity: 1;
}

.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.progress-step.active .step-circle {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
}

.progress-step.completed .step-circle {
  background: #10b981;
  color: #fff;
}

.step-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.progress-line {
  flex: 1;
  height: 2px;
  background: #e5e7eb;
  margin: 0 16px;
  max-width: 100px;
  transition: background 0.3s ease;
}

.progress-line.active {
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

/* ============================================
   SECTION TITLE
   ============================================ */
.section-title {
  margin-bottom: 20px;
}

/* ============================================
   FORM FIELDS
   ============================================ */
.form-label-custom {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  letter-spacing: 0.02em;
}

.custom-select-wrapper {
  position: relative;
}

.form-select-custom,
.form-input-custom {
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  transition: all 0.2s ease;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
}

.form-select-custom {
  padding-right: 42px;
  cursor: pointer;
}

.form-select-custom:focus,
.form-input-custom:focus,
.form-textarea-custom:focus {
  border-color: #6366f1;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-select-custom.is-invalid,
.form-input-custom.is-invalid,
.form-textarea-custom.is-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.08);
}

/* Verified input styling */
.form-input-custom.is-verified {
  border-color: #10b981;
  background: #f0fdf4;
}

.form-input-custom.is-verified:focus {
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  border-color: #10b981;
}

/* Readonly styling */
.form-input-custom.is-readonly,
.form-select-custom.is-readonly {
  background: #666;
  border-color: #fbfbfb;
  color: #fbfbfb;
  cursor: not-allowed;
}

.form-select-custom:disabled {
  background: #666;
  border-color: #fbfbfb;
  color: #fbfbfb;
  cursor: not-allowed;
  opacity: 1;
}

.select-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.input-with-icon {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.input-with-icon .form-input-custom {
  padding-left: 42px;
}

/* Status icon inside input (right side) */
.input-status-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  animation: popIn 0.3s ease;
}

@keyframes popIn {
  0% { transform: translateY(-50%) scale(0); }
  70% { transform: translateY(-50%) scale(1.2); }
  100% { transform: translateY(-50%) scale(1); }
}

.form-textarea-custom {
  width: 100%;
  padding: 14px 16px;
  font-size: 14px;
  color: #1f2937;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  transition: all 0.2s ease;
  outline: none;
  resize: vertical;
  min-height: 140px;
  font-family: inherit;
  line-height: 1.6;
}

.error-text {
  display: block;
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
}

.verified-text {
  display: block;
  color: #10b981;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 600;
}

.readonly-hint {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-top: 4px;
  font-style: italic;
}

/* Auto-filled badge */
.auto-filled-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  background: #ecfdf5;
  color: #059669;
  font-size: 10px;
  font-weight: 700;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border: 1px solid #a7f3d0;
}

/* ============================================
   STUDENT INFO CARD
   ============================================ */
.student-info-card {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px solid #a7f3d0;
  border-radius: 12px;
  padding: 16px 20px;
}

.student-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
}

.student-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #bbf7d0;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
}

.meta-item svg {
  color: #059669;
  flex-shrink: 0;
}

/* ============================================
   REVIEW SUMMARY
   ============================================ */
.review-summary {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.summary-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.summary-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}

.priority-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.priority-high {
  background: #fef2f2;
  color: #dc2626;
}

.priority-medium {
  background: #fffbeb;
  color: #d97706;
}

.priority-low {
  background: #f0fdf4;
  color: #16a34a;
}

/* ============================================
   BUTTONS
   ============================================ */
.form-actions {
  display: flex;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #f3f4f6;
}

.btn-primary-custom,
.btn-secondary-custom,
.btn-submit-custom {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary-custom {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
}

.btn-primary-custom:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}

.btn-primary-custom:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary-custom {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary-custom:hover {
  background: #e5e7eb;
}

.btn-submit-custom {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
}

.btn-submit-custom:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
}

.btn-submit-custom:disabled,
.btn-secondary-custom:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* ============================================
   SUCCESS OVERLAY / MODAL
   ============================================ */
.success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.success-modal {
  background: #fff;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  animation: modalPop 0.3s ease;
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

@keyframes modalPop {
  0% {
    transform: scale(0.9);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ============================================
   TRANSITIONS
   ============================================ */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 576px) {
  .ticket-form-card {
    padding: 20px;
    border-radius: 12px;
  }

  .form-progress {
    padding: 0 0 20px;
  }

  .step-label {
    display: none;
  }

  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }

  .student-meta {
    flex-direction: column;
    gap: 8px;
  }

  .form-actions {
    flex-wrap: wrap;
    gap: 10px;
  }

  .form-actions .ms-auto {
    margin-left: 0 !important;
    width: 100%;
    justify-content: flex-end;
  }

  .logged-in-banner {
    font-size: 13px;
    padding: 12px 16px;
  }
}
</style>